// Feedback Dialog Component
// Two-panel interface with conversation list and message view
/* eslint-disable max-lines-per-function */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { supabase } from '@/lib/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  MessageCircle,
  Plus,
  Clock,
  CheckCircle,
  Archive,
  AlertTriangle,
  User,
  Calendar,
  Lightbulb
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ConversationView } from './ConversationView';
import { NewFeedbackForm } from './NewFeedbackForm';
import {
  useFeedbackDialog,
  useAddMessage,
  useCreateFeedback,
  useFeedbackCategories,
  useCloseFeedback,
  useDeleteFeedback,
  useFeedbackUnread,
  useConversationReadTracking
} from '../hooks/useFeedback';
import {
  ARCHIVE_AFTER_DAYS,
  featureRequestBadge,
  isConfirmedFeatureRequest
} from '../utils/feedbackBuckets';
import { UnreadDot, UnreadTitle, unreadRowCls } from './UnreadIndicator';
import type {
  FeedbackDialogProps,
  FeedbackConversation,
  NewFeedbackFormData,
  MessageFormData
} from '../types';

// Helper function to safely format dates
const safeFormatDate = (
  dateString: string | null | undefined,
  formatFn: (date: Date, options?: { addSuffix?: boolean }) => string,
  fallback: string = 'Unknown',
  options?: { addSuffix?: boolean }
): string => {
  if (!dateString) return fallback;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return fallback;
    }
    return formatFn(date, options);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return fallback;
  }
};

type ActiveTab = 'active' | 'feature_requests' | 'resolved';

interface FeedbackDialogState {
  selectedConversationId?: string;
  isCreatingNew: boolean;
  activeTab: ActiveTab;
  newConversationData?: NewFeedbackFormData;
  showSuccessMessage?: boolean;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  isOpen,
  onClose,
  initialConversationId
}) => {
  const { user } = useAuth();
  const [state, setState] = useState<FeedbackDialogState>({
    selectedConversationId: initialConversationId,
    isCreatingNew: false,
    activeTab: 'active',
    showSuccessMessage: false
  });
  const [tenantId, setTenantId] = useState<string | undefined>();

  // Get tenant ID from JWT or user_approvals
  useEffect(() => {
    const getTenantId = async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      try {
        // Try to get from JWT first
        const payload = JSON.parse(atob(session.access_token.split('.')[1]));
        if (payload.tenant_id) {
          setTenantId(payload.tenant_id);
          return;
        }

        // Fallback: get from user_approvals
        const { data } = await supabase
          .from('user_approvals')
          .select('tenant_id')
          .eq('user_id', payload.sub)
          .eq('status', 'approved')
          .maybeSingle();

        if (data?.tenant_id) {
          setTenantId(data.tenant_id);
        }
      } catch (error) {
        console.error('[FeedbackDialog] Error getting tenant ID:', error);
      }
    };

    if (isOpen) {
      getTenantId();
    }
  }, [isOpen]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setState({
        selectedConversationId: initialConversationId,
        isCreatingNew: false,
        activeTab: 'active',
        showSuccessMessage: false
      });
    } else {
      setState({
        selectedConversationId: undefined,
        isCreatingNew: false,
        activeTab: 'active',
        showSuccessMessage: false
      });
    }
  }, [isOpen, initialConversationId]);

  // Hooks for data fetching
  const {
    activeConversations,
    featureRequestConversations,
    resolvedConversations,
    selectedConversation,
    conversationsLoading,
    selectedLoading,
    conversationsError,
    refetchConversations,
    prefetchConversation
  } = useFeedbackDialog(state.selectedConversationId, user?.id, {
    refetchInterval: 10000,
    enabled: isOpen
  });

  const { data: categories } = useFeedbackCategories();
  const { unreadCounts } = useFeedbackUnread(user?.id, { refetchInterval: 10000, enabled: isOpen });
  const { newMessagesSince } = useConversationReadTracking(selectedConversation, user?.id);
  // The open conversation is being read, so it never shows as unread
  const unreadCountFor = (id: string) =>
    id === state.selectedConversationId ? 0 : (unreadCounts[id] ?? 0);
  const hasUnread = (list: FeedbackConversation[]) => list.some(c => unreadCountFor(c.id) > 0);
  const createFeedbackMutation = useCreateFeedback();
  const addMessageMutation = useAddMessage();
  const closeFeedbackMutation = useCloseFeedback();
  const deleteFeedbackMutation = useDeleteFeedback();

  // Handlers
  const handleConversationSelect = (conversationId: string) => {
    setState(prev => ({
      ...prev,
      selectedConversationId: conversationId,
      isCreatingNew: false
    }));
  };

  const handleTabChange = (value: string) => {
    const newTab = value as ActiveTab;
    const tabLists: Record<ActiveTab, FeedbackConversation[]> = {
      active: activeConversations,
      feature_requests: featureRequestConversations,
      resolved: resolvedConversations
    };
    const targetList = tabLists[newTab];
    const selectedInTargetList =
      state.selectedConversationId && targetList.some(c => c.id === state.selectedConversationId);

    setState(prev => ({
      ...prev,
      activeTab: newTab,
      selectedConversationId: selectedInTargetList ? prev.selectedConversationId : undefined
    }));
  };

  const handleCreateNew = () => {
    setState(prev => ({
      ...prev,
      isCreatingNew: true,
      selectedConversationId: undefined
    }));
  };

  const handleNewFeedbackSubmit = async (data: NewFeedbackFormData) => {
    try {
      const result = await createFeedbackMutation.mutateAsync(data);

      // Show success message first
      setState(prev => ({
        ...prev,
        showSuccessMessage: true,
        newConversationData: data
      }));

      // Immediately refetch conversations to show the new feedback
      await refetchConversations();

      // After 3 seconds, switch to the conversation view
      setTimeout(() => {
        setState(prev => ({
          ...prev,
          selectedConversationId: result.conversation.id,
          isCreatingNew: false,
          showSuccessMessage: false
        }));
      }, 3000);
    } catch (error) {
      console.error('Error creating feedback:', error);
    }
  };

  const handleSendMessage = async (messageData: MessageFormData) => {
    if (!state.selectedConversationId) return;

    try {
      await addMessageMutation.mutateAsync({
        conversation_id: state.selectedConversationId,
        message: messageData.message,
        ...(messageData.attachments && { attachments: messageData.attachments })
      });
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleCloseFeedback = async () => {
    if (!state.selectedConversationId) return;

    try {
      await closeFeedbackMutation.mutateAsync(state.selectedConversationId);

      // Stay in dialog but deselect the conversation and refresh the list
      setState(prev => ({
        ...prev,
        selectedConversationId: undefined
      }));

      // Refresh the conversations list to show updated status
      await refetchConversations();
    } catch (error) {
      console.error('Error closing feedback:', error);
    }
  };

  const handleDeleteFeedback = async () => {
    if (!state.selectedConversationId) return;

    const conversationIdToDelete = state.selectedConversationId;

    try {
      // Clear selection first to prevent refetch attempts
      setState(prev => ({
        ...prev,
        selectedConversationId: undefined
      }));

      await deleteFeedbackMutation.mutateAsync(conversationIdToDelete);

      // Refresh the conversations list to remove the deleted conversation
      await refetchConversations();
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const handleClose = () => {
    setState({
      selectedConversationId: undefined,
      isCreatingNew: false
    });
    onClose();
  };

  // Render conversation list item
  const renderConversationItem = (conversation: FeedbackConversation) => {
    const isSelected = conversation.id === state.selectedConversationId;
    const unreadCount = unreadCountFor(conversation.id);
    const statusConfig = {
      open: {
        label: 'Open',
        color: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
        icon: MessageCircle
      },
      in_progress: {
        label: 'In Progress',
        color: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
        icon: Clock
      },
      resolved: {
        label: 'Resolved',
        color: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
        icon: CheckCircle
      },
      archived: {
        label: 'Archived',
        color: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200',
        icon: Archive
      }
    };

    const isFeatureRequest = isConfirmedFeatureRequest(conversation);
    const status = isFeatureRequest ? featureRequestBadge : statusConfig[conversation.status];
    const StatusIcon = status.icon;

    return (
      <Card
        key={conversation.id}
        className={cn(
          'cursor-pointer transition-all hover:shadow-md',
          unreadRowCls(unreadCount),
          isSelected && 'ring-2 ring-primary shadow-md bg-accent/5'
        )}
        onClick={() => handleConversationSelect(conversation.id)}
        onMouseEnter={() => prefetchConversation(conversation.id)}
      >
        <CardContent className='p-4'>
          <div className='space-y-4'>
            {/* Header with Title and Status */}
            <div className='flex items-start justify-between gap-3'>
              <UnreadTitle
                title={conversation.title}
                unreadCount={unreadCount}
                className='text-sm'
              />
              <Badge
                variant='outline'
                className={cn('text-xs flex items-center gap-1 shrink-0', status.color)}
              >
                <StatusIcon className='h-3 w-3' />
                {status.label}
              </Badge>
            </div>

            {/* User and Time Info */}
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-1 text-xs text-muted-foreground min-w-0 flex-1'>
                <User className='h-3 w-3 shrink-0' />
                <span className='truncate'>
                  {conversation.user_full_name || conversation.user_email || 'Unknown user'}
                </span>
              </div>
              <div className='flex items-center gap-1 text-xs text-muted-foreground shrink-0 ml-2'>
                <Calendar className='h-3 w-3' />
                <span className='whitespace-nowrap'>
                  {safeFormatDate(conversation.updated_at, formatDistanceToNow, 'Unknown time', {
                    addSuffix: true
                  })}
                </span>
              </div>
            </div>

            {/* Bottom Row: Priority and Category */}
            <div className='flex items-center gap-2'>
              {/* Priority indicator */}
              {conversation.priority !== 'medium' && (
                <div className='flex items-center gap-1'>
                  <AlertTriangle
                    className={cn(
                      'h-3 w-3',
                      conversation.priority === 'urgent'
                        ? 'text-red-700 dark:text-red-400'
                        : conversation.priority === 'high'
                          ? 'text-orange-700 dark:text-orange-400'
                          : conversation.priority === 'low'
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-blue-700 dark:text-blue-400'
                    )}
                  />
                  <span
                    className={cn(
                      'text-xs font-semibold capitalize',
                      conversation.priority === 'urgent'
                        ? 'text-red-700 dark:text-red-400'
                        : conversation.priority === 'high'
                          ? 'text-orange-700 dark:text-orange-400'
                          : conversation.priority === 'low'
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-blue-700 dark:text-blue-400'
                    )}
                  >
                    {conversation.priority}
                  </span>
                </div>
              )}

              {/* Category (the status badge already says "Feature request") */}
              {conversation.category_display_name && !isFeatureRequest && (
                <div className='flex items-center gap-1'>
                  <span className='text-xs text-muted-foreground'>
                    {conversation.category_display_name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Tab body: loading, error, empty state, or the list
  const renderConversationList = (
    conversations: FeedbackConversation[],
    empty: { icon: typeof MessageCircle; title: string; text: string }
  ) => {
    const EmptyIcon = empty.icon;
    let content: React.ReactNode;
    if (conversationsLoading) {
      content = (
        <div className='space-y-3'>
          {['skeleton-1', 'skeleton-2', 'skeleton-3'].map(id => (
            <Skeleton key={id} className='h-24 w-full' />
          ))}
        </div>
      );
    } else if (conversationsError) {
      content = (
        <Card>
          <CardContent className='p-4 text-center'>
            <p className='text-sm text-destructive'>
              Error loading conversations: {conversationsError.message}
            </p>
          </CardContent>
        </Card>
      );
    } else if (conversations.length === 0) {
      content = (
        <Card>
          <CardContent className='p-6 text-center'>
            <EmptyIcon className='h-8 w-8 mx-auto text-muted-foreground mb-2' />
            <p className='text-sm font-medium mb-1'>{empty.title}</p>
            <p className='text-sm text-muted-foreground'>{empty.text}</p>
          </CardContent>
        </Card>
      );
    } else {
      content = <div className='space-y-4'>{conversations.map(renderConversationItem)}</div>;
    }

    return (
      <ScrollArea className='flex-1 min-h-0'>
        <div className='space-y-3 pr-4'>{content}</div>
      </ScrollArea>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='max-w-7xl h-[90vh] p-0 overflow-hidden flex flex-col'>
        <DialogHeader className='p-6 pb-4 flex-shrink-0'>
          <DialogTitle className='flex items-center gap-2'>
            <MessageCircle className='h-5 w-5' />
            Feedback & Support
          </DialogTitle>
          <DialogDescription>
            Share your feedback, report issues, or request new features. Our team will respond to
            help improve your experience.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-1 min-h-0 overflow-hidden px-6 pb-6'>
          {/* Left Panel - Conversation List */}
          <div className='w-1/3 flex flex-col min-h-0 min-w-0 pr-4'>
            {/* New Feedback Button */}
            <Button
              onClick={handleCreateNew}
              className='mb-4 h-10 flex-shrink-0'
              disabled={createFeedbackMutation.isPending}
            >
              <Plus className='h-4 w-4 mr-2' />
              New Feedback
            </Button>

            {/* Conversations List with Tabs */}
            <div className='flex-1 min-h-0 flex flex-col overflow-hidden'>
              <Tabs
                value={state.activeTab}
                onValueChange={handleTabChange}
                className='flex-1 min-h-0 flex flex-col overflow-hidden'
              >
                <TabsList className='flex-shrink-0 w-full mb-3'>
                  <TabsTrigger value='active' className='flex-1'>
                    <MessageCircle className='h-3.5 w-3.5 mr-1.5' />
                    Active ({activeConversations.length})
                    {hasUnread(activeConversations) && <UnreadDot className='ml-1.5' />}
                  </TabsTrigger>
                  <TabsTrigger value='feature_requests' className='flex-1' title='Feature requests'>
                    <Lightbulb className='h-3.5 w-3.5 mr-1.5' />
                    <span className='hidden xl:inline'>Feature requests</span>
                    <span className='xl:hidden'>Features</span>
                    &nbsp;({featureRequestConversations.length})
                    {hasUnread(featureRequestConversations) && <UnreadDot className='ml-1.5' />}
                  </TabsTrigger>
                  <TabsTrigger value='resolved' className='flex-1'>
                    <CheckCircle className='h-3.5 w-3.5 mr-1.5' />
                    Resolved ({resolvedConversations.length})
                    {hasUnread(resolvedConversations) && <UnreadDot className='ml-1.5' />}
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value='active'
                  className={
                    'flex-1 min-h-0 mt-0 data-[state=inactive]:hidden flex flex-col overflow-hidden'
                  }
                >
                  {renderConversationList(activeConversations, {
                    icon: MessageCircle,
                    title: 'No active feedback',
                    text: 'Your open and in-progress feedback will appear here.'
                  })}
                </TabsContent>

                <TabsContent
                  value='feature_requests'
                  className={
                    'flex-1 min-h-0 mt-0 data-[state=inactive]:hidden flex flex-col overflow-hidden'
                  }
                >
                  <p className='text-xs text-muted-foreground mb-2 flex-shrink-0'>
                    Ideas our product team has added to the list
                  </p>
                  {renderConversationList(featureRequestConversations, {
                    icon: Lightbulb,
                    title: 'No feature requests',
                    text: "Ideas you've shared that made our product list appear here."
                  })}
                </TabsContent>

                <TabsContent
                  value='resolved'
                  className={
                    'flex-1 min-h-0 mt-0 data-[state=inactive]:hidden flex flex-col overflow-hidden'
                  }
                >
                  <p className='text-xs text-muted-foreground mb-2 flex-shrink-0'>
                    Showing feedback resolved in the last {ARCHIVE_AFTER_DAYS} days. Older feedback
                    is archived and kept on file.
                  </p>
                  {renderConversationList(resolvedConversations, {
                    icon: CheckCircle,
                    title: 'No resolved feedback',
                    text: 'Your resolved feedback will appear here.'
                  })}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <Separator orientation='vertical' className='mx-4 flex-shrink-0' />

          {/* Right Panel - Conversation View or New Feedback Form */}
          <div
            className={cn(
              'flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden',
              'bg-muted/50 rounded-lg p-4'
            )}
          >
            {state.isCreatingNew ? (
              <NewFeedbackForm
                categories={categories || []}
                onSubmit={handleNewFeedbackSubmit}
                onCancel={() =>
                  setState(prev => ({ ...prev, isCreatingNew: false, showSuccessMessage: false }))
                }
                loading={createFeedbackMutation.isPending}
                showSuccessMessage={state.showSuccessMessage || false}
                tenantId={tenantId}
                userId={user?.id}
              />
            ) : selectedConversation ? (
              <ConversationView
                conversation={selectedConversation}
                currentUserId={user?.id}
                onSendMessage={handleSendMessage}
                loading={selectedLoading}
                onCloseFeedback={handleCloseFeedback}
                onDeleteFeedback={handleDeleteFeedback}
                isOwner={user?.id === selectedConversation.user_id}
                {...(newMessagesSince && { newMessagesSince })}
                {...(tenantId && { tenantId })}
                {...(user?.id && { userId: user.id })}
              />
            ) : (
              <div className='flex flex-col items-center justify-center h-full text-center'>
                <MessageCircle className='h-12 w-12 text-muted-foreground mb-4' />
                <h3 className='text-lg font-semibold mb-2'>Select a conversation</h3>
                <p className='text-muted-foreground mb-4'>
                  Choose a conversation from the list to view messages and replies.
                </p>
                <Button onClick={handleCreateNew} variant='outline'>
                  <Plus className='h-4 w-4 mr-2' />
                  Create New Feedback
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
